import React from 'react';

import { Box, Button, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { PlatformStaffListDocument, useSendInvitesToClearVerifyStaffMutation } from 'src/api';
import { AutocompleteField } from 'src/components/finalForm';
import { EMAILS_ADDRESSES_FIELD_HELPER_TEXT } from 'src/constants/misc';
import { useNotification } from 'src/hooks/app';

import { InviteFormFields, InviteFormValues } from './types';
import { inviteFormValidationSchema } from './utils';

interface ClearVerifyStaffInviteManagersFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const ClearVerifyStaffInviteManagersForm: React.FC<
  ClearVerifyStaffInviteManagersFormProps
> = ({ closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  const [sendInvitesToClearVerifyStaffMutation] = useSendInvitesToClearVerifyStaffMutation({
    refetchQueries: [PlatformStaffListDocument],
    context: createApolloContext({ successMessage: 'Invitations sent' }),
  });

  const onSubmit = React.useCallback(
    async (formValues: InviteFormValues) => {
      switchClosability(false);

      try {
        await sendInvitesToClearVerifyStaffMutation({
          variables: {
            managers: formValues.emails.map(email => ({ email })),
          },
        });

        switchClosability(true);
        closeModal();
      } catch {
        switchClosability(true);
      }
    },
    [sendInvitesToClearVerifyStaffMutation, closeModal, switchClosability],
  );

  return (
    <Box>
      <Typography variant="body1" fontWeight="bold">
        Invite Managers by using their email address
      </Typography>

      <Form<InviteFormValues>
        onSubmit={onSubmit}
        subscription={{ valid: true, values: true, submitting: true }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(inviteFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Box>
                <Field
                  name={InviteFormFields.emails}
                  component={AutocompleteField}
                  label="Email addresses"
                  fullWidth
                  multiple
                  freeSolo
                  options={[]}
                  helperText={EMAILS_ADDRESSES_FIELD_HELPER_TEXT}
                  placeholder="Type Email Address"
                  required
                />
              </Box>

              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => closeModal()}
                  disabled={submitting}
                >
                  Close
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting}
                  loading={submitting}
                >
                  Send invite
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};
